import { CSSProperties, PointerEvent as ReactPointerEvent, useRef, useState } from 'react';
import { clampTime, formatSeconds, trackTimeFromClientX } from '../utils/clipTime';

// 起止手柄之间的最小间隔（秒），防止两个手柄交叉；片段时长合法性由 clipDurationError 另行校验
const MIN_HANDLE_GAP = 0.1;
const TRACK_HEIGHT = 44;

type DragKind = 'start' | 'end' | 'seek';

interface ClipRangeTrackProps {
  duration: number;
  // single 变体（单帧模式）：无选区/起止手柄，只有一个跟随 currentTime 的可拖拽游标
  variant?: 'range' | 'single';
  startTime?: number;
  endTime?: number;
  currentTime: number;
  disabled?: boolean;
  onChangeStart?: (t: number) => void;
  onChangeEnd?: (t: number) => void;
  onSeek: (t: number) => void;
}

const handleBaseStyle: CSSProperties = {
  position: 'absolute', top: 0, bottom: 0, width: '12px',
  background: 'var(--primary, #3b82f6)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  touchAction: 'none', zIndex: 2,
};

// 视频编辑器式时间轴：单条轨道 + 高亮选区 + 两端拖拽手柄 + 秒级刻度 + 播放位置指示线。
// 拖动手柄时通过 onSeek 让视频实时预览切点画面；点击/拖动选区外的轨道只 seek 预览不改选区。
export default function ClipRangeTrack({
  duration, variant = 'range', startTime = 0, endTime = 0, currentTime, disabled,
  onChangeStart, onChangeEnd, onSeek,
}: ClipRangeTrackProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState<DragKind | null>(null);
  const isRange = variant === 'range';

  const pct = (t: number) => (duration > 0 ? Math.min(100, Math.max(0, (t / duration) * 100)) : 0);

  const timeAt = (clientX: number): number => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    return trackTimeFromClientX(clientX, rect.left, rect.width, duration);
  };

  const applyDrag = (kind: DragKind, clientX: number) => {
    const t = timeAt(clientX);
    if (kind === 'start') {
      const v = Math.min(t, clampTime(endTime - MIN_HANDLE_GAP, duration));
      onChangeStart?.(v);
      onSeek(v);
    } else if (kind === 'end') {
      const v = Math.max(t, clampTime(startTime + MIN_HANDLE_GAP, duration));
      onChangeEnd?.(v);
      onSeek(v);
    } else {
      onSeek(t);
    }
  };

  const beginDrag = (kind: DragKind) => (e: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled || duration <= 0) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setDragging(kind);
    applyDrag(kind, e.clientX);
  };

  const moveDrag = (kind: DragKind) => (e: ReactPointerEvent<HTMLDivElement>) => {
    if (dragging !== kind) return;
    applyDrag(kind, e.clientX);
  };

  const endDrag = () => setDragging(null);

  // 刻度：每秒一格；时长 >12s 每 5s 标数字，>6s 每 2s，否则每 1s
  const labelStep = duration > 12 ? 5 : duration > 6 ? 2 : 1;
  const ticks: number[] = [];
  for (let s = 0; s <= Math.floor(duration); s++) ticks.push(s);

  return (
    <div
      ref={trackRef}
      onPointerDown={beginDrag('seek')}
      onPointerMove={moveDrag('seek')}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      style={{
        position: 'relative', height: `${TRACK_HEIGHT}px`, marginTop: '0.6rem',
        background: 'var(--surface-2, #f0f0ef)', border: '1px solid var(--border)',
        borderRadius: '6px', overflow: 'hidden', touchAction: 'none',
        cursor: disabled ? 'default' : 'pointer', userSelect: 'none',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {/* 秒级刻度与标签 */}
      {ticks.map(s => (
        <div key={s} style={{ position: 'absolute', left: `${pct(s)}%`, top: 0, bottom: 0, pointerEvents: 'none' }}>
          <div style={{
            position: 'absolute', top: 0, width: '1px',
            height: s % labelStep === 0 ? '10px' : '6px',
            background: 'var(--text-3)', opacity: 0.5,
          }} />
          {s % labelStep === 0 && (
            <span style={{ position: 'absolute', top: '10px', left: '3px', fontSize: '0.62rem', color: 'var(--text-3)' }}>
              {s}s
            </span>
          )}
        </div>
      ))}

      {/* 选区高亮（range 变体） */}
      {isRange && (
        <div style={{
          position: 'absolute', top: 0, bottom: 0,
          left: `${pct(startTime)}%`, width: `${Math.max(0, pct(endTime) - pct(startTime))}%`,
          background: 'rgba(59, 130, 246, 0.28)',
          borderTop: '2px solid var(--primary, #3b82f6)', borderBottom: '2px solid var(--primary, #3b82f6)',
          pointerEvents: 'none', boxSizing: 'border-box',
        }} />
      )}

      {/* 播放位置指示线 */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0, left: `${pct(currentTime)}%`,
        width: '1px', background: 'var(--danger, #dc2626)', opacity: 0.8,
        pointerEvents: 'none', zIndex: 1,
      }} />

      {isRange ? (
        <>
          {/* 起点手柄 */}
          <div
            onPointerDown={beginDrag('start')}
            onPointerMove={moveDrag('start')}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            style={{
              ...handleBaseStyle,
              left: `calc(${pct(startTime)}% - 6px)`,
              borderRadius: '4px 0 0 4px',
              cursor: disabled ? 'default' : 'ew-resize',
            }}
            title={formatSeconds(startTime)}
          >
            <div style={{ width: '2px', height: '60%', background: 'rgba(255,255,255,0.85)', borderRadius: '1px' }} />
          </div>

          {/* 终点手柄 */}
          <div
            onPointerDown={beginDrag('end')}
            onPointerMove={moveDrag('end')}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            style={{
              ...handleBaseStyle,
              left: `calc(${pct(endTime)}% - 6px)`,
              borderRadius: '0 4px 4px 0',
              cursor: disabled ? 'default' : 'ew-resize',
            }}
            title={formatSeconds(endTime)}
          >
            <div style={{ width: '2px', height: '60%', background: 'rgba(255,255,255,0.85)', borderRadius: '1px' }} />
          </div>
        </>
      ) : (
        /* 单游标（single 变体，单帧模式）：跟随 currentTime 的可拖拽播放头 */
        <div
          onPointerDown={beginDrag('seek')}
          onPointerMove={moveDrag('seek')}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          style={{
            ...handleBaseStyle,
            left: `calc(${pct(currentTime)}% - 6px)`,
            borderRadius: '4px',
            cursor: disabled ? 'default' : 'ew-resize',
          }}
          title={formatSeconds(currentTime)}
        >
          <div style={{ width: '2px', height: '60%', background: 'rgba(255,255,255,0.85)', borderRadius: '1px' }} />
        </div>
      )}
    </div>
  );
}
